import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CreditCard, Repeat, Server } from 'lucide-react';
import { Badge, Card, CardBody, CardHeader, CardTitle, EmptyState, Spinner } from '@pnl/ui';
import type { DelinquencyItem, DelinquencyType } from '@pnl/sdk';
import { api } from '../lib/api';

const typeLabel: Record<DelinquencyType, string> = {
  subscription: 'Assinatura',
  platform: 'Plataforma',
  replicator: 'Replicador',
};

const typeIcon: Record<DelinquencyType, React.ComponentType<{ size?: number }>> = {
  subscription: CreditCard,
  platform: Server,
  replicator: Repeat,
};

const statusTone = (status: string): 'warning' | 'danger' | 'neutral' => {
  if (status === 'SUSPENDED' || status === 'EXPIRED') return 'danger';
  if (status === 'OVERDUE' || status === 'PAST_DUE') return 'warning';
  return 'neutral';
};

const statusLabel: Record<string, string> = {
  PAST_DUE: 'Atrasado',
  EXPIRED: 'Expirado',
  OVERDUE: 'Vencido',
  SUSPENDED: 'Suspenso',
};

function daysOverdue(dueDate: string | null): number | null {
  if (!dueDate) return null;
  const diff = Date.now() - new Date(dueDate).getTime();
  return Math.floor(diff / 86_400_000);
}

const FILTERS: { value: DelinquencyType | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'Todos' },
  { value: 'subscription', label: 'Assinaturas' },
  { value: 'platform', label: 'Plataformas' },
  { value: 'replicator', label: 'Replicador' },
];

export function DelinquenciesPage() {
  const [filter, setFilter] = useState<DelinquencyType | 'ALL'>('ALL');

  const query = useQuery({
    queryKey: ['admin', 'delinquencies'],
    queryFn: () => api.listDelinquencies(),
  });

  const all = query.data?.items ?? [];
  const items = filter === 'ALL' ? all : all.filter((i) => i.type === filter);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">
          Financeiro
        </p>
        <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
          Inadimplência
        </h1>
        <p className="text-ink-500 mt-1 text-sm">
          Visão consolidada das cobranças vencidas — assinaturas de plano, mensalidades de
          plataforma e do replicador num lugar só. Aja em cada fluxo na sua aba.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex w-full items-center justify-between gap-4">
            <CardTitle>
              {items.length} pendência(s){query.isLoading ? '…' : ''}
            </CardTitle>
            <div className="flex gap-1">
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setFilter(f.value)}
                  className={
                    'rounded-md px-3 py-1.5 text-xs font-medium transition-colors ' +
                    (filter === f.value
                      ? 'bg-brand-500 text-white'
                      : 'text-ink-500 hover:bg-ink-100')
                  }
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {query.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<AlertTriangle size={28} />}
              title="Nenhuma inadimplência"
              description="Cobranças vencidas (assinatura, plataforma ou replicador) aparecem aqui."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-ink-100 text-2xs text-ink-500 border-b text-left font-semibold uppercase tracking-widest">
                    <th className="px-3 py-2.5">Trader</th>
                    <th className="px-3 py-2.5">Tipo</th>
                    <th className="px-3 py-2.5">Cobrança</th>
                    <th className="px-3 py-2.5">Status</th>
                    <th className="px-2 py-2.5">Vencimento</th>
                    <th className="px-2 py-2.5 text-right">Atraso</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((d) => (
                    <DelinquencyRow key={`${d.type}-${d.id}`} d={d} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function DelinquencyRow({ d }: { d: DelinquencyItem }) {
  const Icon = typeIcon[d.type];
  const days = daysOverdue(d.dueDate);
  return (
    <tr className="border-ink-100 border-b">
      <td className="px-3 py-2.5">
        <p className="text-ink-900 font-medium">{d.traderName || d.traderEmail}</p>
        <p className="text-ink-500 text-xs">{d.traderEmail}</p>
      </td>
      <td className="px-3 py-2.5">
        <span className="text-ink-600 inline-flex items-center gap-1.5 text-xs font-medium">
          <Icon size={13} /> {typeLabel[d.type]}
        </span>
      </td>
      <td className="text-ink-700 px-3 py-2.5">{d.label}</td>
      <td className="px-3 py-2.5">
        <Badge tone={statusTone(d.status)}>{statusLabel[d.status] ?? d.status}</Badge>
      </td>
      <td className="text-ink-600 px-2 py-2.5 text-xs tabular-nums">
        {d.dueDate ? new Date(d.dueDate).toLocaleDateString('pt-BR') : '—'}
      </td>
      <td className="px-2 py-2.5 text-right text-xs tabular-nums">
        {days !== null && days > 0 ? (
          <span className="text-danger-600 font-semibold">{days}d</span>
        ) : (
          <span className="text-ink-400">—</span>
        )}
      </td>
    </tr>
  );
}
